import { createFileRoute, Link } from '@tanstack/react-router'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  ArrowUpRight,
  ChevronRight,
  FolderOpen,
  Package,
  PackagePlus,
} from 'lucide-react'
import { useState } from 'react'
import { RequestPackageDialog } from '@/components/RequestPackageDialog'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import type { ClientSetup } from '@/lib/types/setups'
import {
  EcosystemMark,
  Eyebrow,
} from '@/components/hawk-primitives'

interface Repo {
  name: string
  ecosystem: string
  upstream_base: string | null
  effective_upstream_base: string
  mount_path: string
  package_count: number
  last_fetched_at: string | null
  setups: ClientSetup[]
}

export const Route = createFileRoute('/_app/packages/')({
  component: PackagesPage,
})

function PackagesPage() {
  const [requestOpen, setRequestOpen] = useState(false)

  // Every org member (down to viewer) can hit this endpoint - GET is open to
  // members; the POST/PATCH/DELETE on the same path stay admin-only.
  const repos = useOrgQuery<Repo[]>(['admin-repositories'], '/admin/repositories')

  const repoOptions = (repos.data ?? []).map((r) => ({
    name: r.name,
    ecosystem: r.ecosystem,
    upstream_base: r.upstream_base,
  }))

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Packages"
        description="Browse repositories mounted on this registry. Each repository proxies an ecosystem and caches install artifacts on demand."
        right={
          repoOptions.length > 0 && (
            <Button onClick={() => setRequestOpen(true)} className="gap-2">
              <PackagePlus className="size-4" strokeWidth={1.5} />
              Request a package
            </Button>
          )
        }
      />

      {repos.isLoading ? (
        <LoadingState />
      ) : repos.isError || (repos.data ?? []).length === 0 ? (
        <EmptyPanel />
      ) : (
        <Loaded repos={repos.data ?? []} />
      )}

      <RequestPackageDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        availableRepos={repoOptions}
      />
    </div>
  )
}

function Loaded({ repos }: { repos: Repo[] }) {
  return (
    <>
      {/* Repository cards */}
      <section>
        <div className="flex items-baseline justify-between mb-5">
          <Eyebrow>Install from a repository</Eyebrow>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground tabular-nums">
            {repos.length.toString().padStart(2, '0')} mounts
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {repos.map((repo, i) => (
            <RepoCard
              key={repo.name}
              repo={repo}
              delayMs={Math.min(i * 50, 400)}
            />
          ))}
        </div>
      </section>
    </>
  )
}


function RepoCard({
  repo,
  delayMs,
}: {
  repo: Repo
  delayMs: number
}) {
  return (
    <Link
      to="/packages/$repo"
      params={{ repo: repo.name }}
      className="group relative border border-border bg-card rounded-md overflow-hidden animate-hawk-rise hover:border-primary/30 hover:bg-popover/30 transition-colors flex flex-col"
      style={{ animationDelay: `${delayMs}ms` }}
    >
      {/* Header */}
      <div className="flex items-start gap-4 p-5 pb-4 border-b border-border">
        <EcosystemMark ecosystem={repo.ecosystem} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base font-medium text-foreground truncate">
              {repo.name}
            </h3>
            <ArrowUpRight
              className="size-3.5 text-muted-foreground/50 group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
              strokeWidth={1.5}
            />
          </div>
          <div
            className="font-mono text-[11px] text-muted-foreground truncate"
            title={repo.effective_upstream_base}
          >
            {repo.effective_upstream_base}
            {!repo.upstream_base && (
              <span className="ml-1.5 text-muted-foreground/60">(default)</span>
            )}
          </div>
        </div>
      </div>

      <dl className="grid grid-cols-2 px-5 py-4 gap-3 flex-1">
        <Stat label="Packages" value={repo.package_count.toLocaleString()} />
        <Stat
          label="Last admission"
          value={repo.last_fetched_at ? formatDate(repo.last_fetched_at) : '-'}
          muted={!repo.last_fetched_at}
        />
      </dl>

      <div className="flex items-center justify-between gap-2 px-5 py-2.5 border-t border-border bg-background/30 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground group-hover:text-foreground transition-colors">
        <span className="inline-flex items-center gap-1.5">
          <FolderOpen className="size-3" strokeWidth={1.5} />
          Browse {repo.package_count.toLocaleString()} packages
        </span>
        <ChevronRight
          className="size-3 transition-transform group-hover:translate-x-0.5"
          strokeWidth={1.5}
        />
      </div>
    </Link>
  )
}

function Stat({
  label,
  value,
  muted,
}: {
  label: string
  value: string
  muted?: boolean
}) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70 mb-1">
        {label}
      </dt>
      <dd
        className={`font-mono text-xs tabular-nums truncate ${muted ? 'text-muted-foreground' : 'text-foreground'}`}
      >
        {value}
      </dd>
    </div>
  )
}

function LoadingState() {
  return (
    <>
      <div className="border border-border rounded-md bg-card/30 grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border mb-10">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="px-7 py-7 hawk-shimmer">
            <div className="h-3 w-20 bg-secondary rounded mb-4" />
            <div className="h-10 w-24 bg-secondary rounded mb-2" />
            <div className="h-3 w-32 bg-secondary rounded" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-[260px] border border-border bg-card rounded-md hawk-shimmer"
          />
        ))}
      </div>
    </>
  )
}

function EmptyPanel() {
  return (
    <div className="relative overflow-hidden border border-border border-dashed rounded-md py-16 px-8 bg-card/30 animate-hawk-rise">
      <div
        className="absolute inset-0 hawk-grid hawk-fade-radial pointer-events-none"
        aria-hidden
      />
      <div className="relative flex flex-col items-center text-center">
        <div className="flex items-center justify-center size-14 rounded-md bg-secondary border border-border mb-5">
          <Package className="size-6 text-muted-foreground" strokeWidth={1.5} />
        </div>
        <p className="font-display text-2xl tracking-tight mb-2 text-foreground/85">
          No repositories yet
        </p>
        <p className="text-sm text-muted-foreground max-w-md">
          An admin needs to configure a repository before packages can be
          installed or cached through this organization.
        </p>
      </div>
    </div>
  )
}
